import React from "react";

/**
 * LoyaltyCoinPanel — checkout widget that lets a signed-in user apply
 * their coins with this business to the current order. Skips rendering
 * entirely for anonymous buyers (business rule: only registered users
 * can redeem, so we don't want to nag them mid-checkout).
 *
 * Fetches `/api/loyalty/preview-redeem` after mount to learn balance +
 * caps. Slider defaults to the max redeemable so the buyer sees the
 * biggest possible discount immediately.
 *
 * Extracted from `pages/Storefront.jsx` during the v142.3 refactor —
 * behaviour is unchanged, only the file location.
 */
export function LoyaltyCoinPanel({ product, subtotalAfterCoupon, coinsToApply,
                                   setCoinsToApply, coinPreview, setCoinPreview }) {
  const [loading, setLoading] = React.useState(true);
  const [authed, setAuthed] = React.useState(false);
  // v142.1 — Prefer `creator_user_id` (public-safe key exposed by the
  // storefront/product-detail endpoints); fall back to legacy `user_id`
  // for defensive compatibility with any older cached payloads.
  const businessUserId = product?.creator_user_id || product?.user_id;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!businessUserId || subtotalAfterCoupon <= 0) {
        setLoading(false);
        return;
      }
      try {
        const params = new URLSearchParams({
          business_user_id: businessUserId,
          order_subtotal_inr: String(subtotalAfterCoupon),
          apply_coins: "0",
        });
        const r = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/api/loyalty/preview-redeem?${params}`,
          {
            credentials: "include",
            // v145.29 — Also attach the Bearer token so the panel
            // works in localStorage-token setups (capacitor / cross-
            // origin browser). Cookie-based sessions still work.
            headers: (typeof localStorage !== "undefined"
                      && localStorage.getItem("creatoraiq_session_token"))
              ? { Authorization: `Bearer ${localStorage.getItem("creatoraiq_session_token")}` }
              : {},
          },
        );
        if (r.status === 401) {
          setAuthed(false);
          return;
        }
        setAuthed(true);
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        setCoinPreview(data);
        // Default the slider to the max redeemable so the user sees
        // the biggest possible discount by default.
        if (data.eligible && data.max_coins_by_cap > 0) {
          setCoinsToApply(Math.min(data.max_coins_by_cap, data.balance));
        }
      } catch { /* silent */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };

  }, [businessUserId, subtotalAfterCoupon]);

  if (loading || !authed || !coinPreview) return null;

  if (!coinPreview.eligible) {
    if (coinPreview.reason === "below_minimum") {
      return (
        <div className="border-2 border-ink bg-cream p-2 text-[11px]" data-testid="storefront-buy-coin-below-min">
          You have <b>{coinPreview.balance}</b> {coinPreview.coin_name}.
          Need <b>{coinPreview.min_coins_to_redeem}</b> to redeem — keep engaging to earn more!
        </div>
      );
    }
    return null;
  }

  const cap = Math.min(coinPreview.max_coins_by_cap, coinPreview.balance);
  const value = Math.max(0, Math.min(coinsToApply, cap));
  const discount = (value * coinPreview.value_per_coin_inr).toFixed(2);

  return (
    <div className="border-2 border-ink bg-sun p-3 space-y-2 shadow-brutalSm" data-testid="storefront-buy-coin-panel">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-black uppercase">Loyalty {coinPreview.coin_name}</p>
        <p className="text-[10px] font-bold">Balance: {coinPreview.balance}</p>
      </div>
      <input
        type="range"
        min={0}
        max={cap}
        step={1}
        value={value}
        onChange={(e) => setCoinsToApply(Number(e.target.value))}
        className="w-full accent-coral"
        data-testid="storefront-buy-coin-slider"
      />
      <div className="flex items-center justify-between text-[11px] font-bold">
        <span>Apply {value} {coinPreview.coin_name}</span>
        <span data-testid="storefront-buy-coin-panel-discount">-₹{discount} off</span>
      </div>
      <p className="text-[10px] text-ink/70">
        Max {coinPreview.max_discount_pct}% of order can be paid with coins. 1 {coinPreview.coin_name === "coins" ? "coin" : coinPreview.coin_name.replace(/s$/, "")} = ₹{coinPreview.value_per_coin_inr}.
      </p>
    </div>
  );
}

export default LoyaltyCoinPanel;
